import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { fetchShiftTemplates, updateShiftTemplates } from "../../../redux/slices/shiftTemplateSlice";

const DEFAULT_TEMPLATES = [
    { id: "morning", label: "Morning", start_time: "06:00", end_time: "14:00", color: "#22C55E" },
    { id: "afternoon", label: "Afternoon", start_time: "14:00", end_time: "22:00", color: "#F59E0B" },
    { id: "night", label: "Night", start_time: "22:00", end_time: "06:00", color: "#6366F1" },
];

const toMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
};

const duration = ({ start_time, end_time }) => (toMinutes(end_time) - toMinutes(start_time) + 1440) % 1440;

const validateTemplates = (templates, t) => {
    if (templates.some((template) => !template.label.trim() || duration(template) === 0)) {
        return t("components.ShiftTemplatesTab.errors.invalidRange");
    }
    const labels = templates.map((template) => template.label.trim().toLocaleLowerCase());
    return new Set(labels).size === labels.length ? null : t("components.ShiftTemplatesTab.errors.duplicateLabels");
};

const ShiftTemplatesTab = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const configuration = useSelector((state) => state.shiftTemplates.item);
    const isLoading = useSelector((state) => state.shiftTemplates.isLoading);
    const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [validationError, setValidationError] = useState("");

    useEffect(() => { dispatch(fetchShiftTemplates()); }, [dispatch]);
    useEffect(() => {
        if (configuration?.templates?.length) setTemplates(configuration.templates.map((template) => ({ ...template })));
    }, [configuration]);

    const filteredTemplates = useMemo(() => {
        const search = searchTerm.trim().toLocaleLowerCase();
        return search ? templates.filter((template) => template.label.toLocaleLowerCase().includes(search)) : templates;
    }, [searchTerm, templates]);
    const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);

    const updateTemplate = (id, field, value) => {
        setValidationError("");
        setTemplates((current) => current.map((template) => template.id === id ? { ...template, [field]: value } : template));
    };
    const addTemplate = () => {
        const template = { id: crypto.randomUUID(), label: t("components.ShiftTemplatesTab.newShift"), start_time: "09:00", end_time: "17:00", color: "#0EA5E9" };
        setTemplates((current) => [...current, template]);
        setSelectedTemplateId(template.id);
    };
    const removeSelectedTemplate = () => {
        if (templates.length === 1) return;
        setTemplates((current) => current.filter((template) => template.id !== selectedTemplateId));
        setSelectedTemplateId(null);
    };
    const handleSave = async () => {
        const normalizedTemplates = templates.map((template) => ({ ...template, label: template.label.trim() }));
        const error = validateTemplates(normalizedTemplates, t);
        if (error) return setValidationError(error);
        setValidationError("");
        await dispatch(updateShiftTemplates(normalizedTemplates));
    };

    if (isLoading && !configuration) return <div className="py-5 text-center"><Spinner animation="border" /></div>;

    return <div className="shift-templates-tab">
        <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4 pb-2 border-bottom">
            <div>
                <h4 className="txt-main fw-bold mb-1">{t("components.ShiftTemplatesTab.title")}</h4>
                <p className="txt-subtle mb-0">{t("components.ShiftTemplatesTab.description")}</p>
            </div>
            <div className="d-flex gap-2">
                <Button variant="outline-danger" className="shadow-sm" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? <Spinner size="sm" animation="border" /> : t("common.actions.save")}
                </Button>
                <Button variant="outline-danger" className="shadow-sm" onClick={addTemplate}>
                    <FiPlus size={18} /> {t("components.ShiftTemplatesTab.addShift")}
                </Button>
            </div>
        </div>

        <div className="atlar-card mb-4">
            <InputGroup className="shadow-none p-2">
                <InputGroup.Text className="bg-transparent border-0"><FaSearch className="txt-subtle" /></InputGroup.Text>
                <Form.Control type="search" placeholder={t("components.ShiftTemplatesTab.search")} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} className="border-0 shadow-none" />
            </InputGroup>
        </div>

        <div className="table-responsive atlar-card">
            <table className="table table-hover table-borderless mb-0 align-middle bg-surface">
                <thead style={{ borderBottom: "var(--border-thin)" }}><tr>
                    <th className="txt-main fw-semibold py-3 px-4 border-0">{t("components.ShiftTemplatesTab.nameLabel")}</th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0">{t("components.ShiftTemplatesTab.startLabel")}</th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0">{t("components.ShiftTemplatesTab.endLabel")}</th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0">{t("components.ShiftTemplatesTab.durationLabel")}</th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0 text-center">{t("components.ShiftTemplatesTab.actions")}</th>
                </tr></thead>
                <tbody>{filteredTemplates.length ? filteredTemplates.map((template) => <tr key={template.id} style={{ borderBottom: "var(--border-thin)" }}>
                    <td className="py-3 px-4 border-0 fw-semibold txt-main"><span className="d-inline-block rounded me-2 align-middle" style={{ width: 10, height: 24, background: template.color }} />{template.label}</td>
                    <td className="py-3 px-4 border-0 txt-subtle">{template.start_time}</td>
                    <td className="py-3 px-4 border-0 txt-subtle">{template.end_time}</td>
                    <td className="py-3 px-4 border-0 txt-subtle">{t("components.ShiftTemplatesTab.duration", { hours: duration(template) / 60 })}</td>
                    <td className="py-3 px-4 border-0 text-center"><Button size="sm" className="txt-inverse fw-semibold px-3" style={{ backgroundColor: "var(--brand-primary)", borderRadius: "var(--btn-radius)" }} onClick={() => setSelectedTemplateId(template.id)}>{t("components.ShiftTemplatesTab.details")}</Button></td>
                </tr>) : <tr><td colSpan="5" className="text-center py-5 txt-subtle bg-surface">{t("components.ShiftTemplatesTab.noResults")}</td></tr>}</tbody>
            </table>
        </div>
        {validationError && <Alert variant="danger" className="mt-3 mb-0">{validationError}</Alert>}

        <Modal show={Boolean(selectedTemplate)} onHide={() => setSelectedTemplateId(null)} centered>
            {selectedTemplate && <><Modal.Header closeButton><Modal.Title>{selectedTemplate.label}</Modal.Title></Modal.Header><Modal.Body>
                <Form.Group className="mb-3"><Form.Label>{t("components.ShiftTemplatesTab.nameLabel")}</Form.Label><Form.Control value={selectedTemplate.label} maxLength={80} onChange={(event) => updateTemplate(selectedTemplate.id, "label", event.target.value)} /></Form.Group>
                <div className="row g-3"><div className="col"><Form.Label>{t("components.ShiftTemplatesTab.startLabel")}</Form.Label><Form.Control type="time" value={selectedTemplate.start_time} onChange={(event) => updateTemplate(selectedTemplate.id, "start_time", event.target.value)} /></div><div className="col"><Form.Label>{t("components.ShiftTemplatesTab.endLabel")}</Form.Label><Form.Control type="time" value={selectedTemplate.end_time} onChange={(event) => updateTemplate(selectedTemplate.id, "end_time", event.target.value)} /></div></div>
                <Form.Group className="mt-3"><Form.Label>{t("components.ShiftTemplatesTab.colorLabel")}</Form.Label><Form.Control type="color" value={selectedTemplate.color} onChange={(event) => updateTemplate(selectedTemplate.id, "color", event.target.value)} /></Form.Group>
            </Modal.Body><Modal.Footer><Button variant="outline-danger" onClick={removeSelectedTemplate} disabled={templates.length === 1}>{t("common.actions.delete")}</Button><Button variant="secondary" onClick={() => setSelectedTemplateId(null)}>{t("common.actions.close")}</Button></Modal.Footer></>}
        </Modal>
    </div>;
};

export default ShiftTemplatesTab;
